<template>
  <div
    class="flex-item"
    :class="[spanClass, alignClass, growClass, shrinkClass]"
    :style="styleObject"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { FlexAlign } from './FlexLayout.vue';

/**
 * Responsive span value: [mobile, tablet, desktop]
 * Mobile: <600px, Tablet: 600-1023px, Desktop: >=1024px
 * Use 0 to hide element at that breakpoint
 */
export type ResponsiveSpan = [number, number, number];

const props = defineProps<{
  /** Column span (1-12) or responsive array [mobile, tablet, desktop] */
  span?: number | ResponsiveSpan;
  /** Whether item should grow to fill available space */
  grow?: boolean | number;
  /** Whether item should shrink when space is limited */
  shrink?: boolean | number;
  /** Fixed width */
  width?: string;
  /** Minimum width */
  minWidth?: string;
  /** Maximum width */
  maxWidth?: string;
  /** Override parent's align for this specific item */
  align?: FlexAlign;
}>();

const spanClass = computed(() => {
  if (!props.span) return '';

  if (Array.isArray(props.span)) {
    const [mobile, tablet, desktop] = props.span;
    return `span-mobile-${mobile} span-tablet-${tablet} span-desktop-${desktop}`;
  }

  return `span-${props.span}`;
});

const alignClass = computed(() => (props.align ? `align-self-${props.align}` : ''));

const growClass = computed(() => {
  if (props.grow === true) return 'grow-1';
  if (typeof props.grow === 'number') return `grow-${props.grow}`;
  return '';
});

const shrinkClass = computed(() => {
  if (props.shrink === true) return 'shrink-1';
  if (typeof props.shrink === 'number') return `shrink-${props.shrink}`;
  return '';
});

const styleObject = computed(() => {
  const styles: Record<string, string> = {};

  if (props.width) styles.width = props.width;
  if (props.minWidth) styles.minWidth = props.minWidth;
  if (props.maxWidth) styles.maxWidth = props.maxWidth;

  return styles;
});
</script>

<style scoped>
.flex-item {
  flex: 0 1 auto;
  max-width: 100%;
  &.span-1 {
    flex: 0 0 calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
    max-width: calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
  }
  &.span-2 {
    flex: 0 0 calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
    max-width: calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
  }
  &.span-3 {
    flex: 0 0 calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
    max-width: calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
  }
  &.span-4 {
    flex: 0 0 calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
    max-width: calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
  }
  &.span-5 {
    flex: 0 0 calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
    max-width: calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
  }
  &.span-6 {
    flex: 0 0 calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
    max-width: calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
  }
  &.span-7 {
    flex: 0 0 calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
    max-width: calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
  }
  &.span-8 {
    flex: 0 0 calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
    max-width: calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
  }
  &.span-9 {
    flex: 0 0 calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
    max-width: calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
  }
  &.span-10 {
    flex: 0 0 calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
    max-width: calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
  }
  &.span-11 {
    flex: 0 0 calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
    max-width: calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
  }
  &.span-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  &.span-mobile-1 {
    flex: 0 0 calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
    max-width: calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
  }
  &.span-mobile-2 {
    flex: 0 0 calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
    max-width: calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
  }
  &.span-mobile-3 {
    flex: 0 0 calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
    max-width: calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
  }
  &.span-mobile-4 {
    flex: 0 0 calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
    max-width: calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
  }
  &.span-mobile-5 {
    flex: 0 0 calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
    max-width: calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
  }
  &.span-mobile-6 {
    flex: 0 0 calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
    max-width: calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
  }
  &.span-mobile-7 {
    flex: 0 0 calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
    max-width: calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
  }
  &.span-mobile-8 {
    flex: 0 0 calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
    max-width: calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
  }
  &.span-mobile-9 {
    flex: 0 0 calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
    max-width: calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
  }
  &.span-mobile-10 {
    flex: 0 0 calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
    max-width: calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
  }
  &.span-mobile-11 {
    flex: 0 0 calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
    max-width: calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
  }
  &.span-mobile-12 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  @media (max-width: 599px) {
    &.span-mobile-0 {
      display: none;
    }
  }

  @media (min-width: 600px) {
    &.span-tablet-1 {
      flex: 0 0 calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
      max-width: calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
    }
    &.span-tablet-2 {
      flex: 0 0 calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
      max-width: calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
    }
    &.span-tablet-3 {
      flex: 0 0 calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
      max-width: calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
    }
    &.span-tablet-4 {
      flex: 0 0 calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
      max-width: calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
    }
    &.span-tablet-5 {
      flex: 0 0 calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
      max-width: calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
    }
    &.span-tablet-6 {
      flex: 0 0 calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
      max-width: calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
    }
    &.span-tablet-7 {
      flex: 0 0 calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
      max-width: calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
    }
    &.span-tablet-8 {
      flex: 0 0 calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
      max-width: calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
    }
    &.span-tablet-9 {
      flex: 0 0 calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
      max-width: calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
    }
    &.span-tablet-10 {
      flex: 0 0 calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
      max-width: calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
    }
    &.span-tablet-11 {
      flex: 0 0 calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
      max-width: calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
    }
    &.span-tablet-12 {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    &.span-tablet-0 {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    &.span-desktop-1 {
      flex: 0 0 calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
      max-width: calc((100% / 12 * 1) - (var(--flex-layout-gap, 0px) * (12 - 1) / 12));
    }
    &.span-desktop-2 {
      flex: 0 0 calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
      max-width: calc((100% / 12 * 2) - (var(--flex-layout-gap, 0px) * (12 - 2) / 12));
    }
    &.span-desktop-3 {
      flex: 0 0 calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
      max-width: calc((100% / 12 * 3) - (var(--flex-layout-gap, 0px) * (12 - 3) / 12));
    }
    &.span-desktop-4 {
      flex: 0 0 calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
      max-width: calc((100% / 12 * 4) - (var(--flex-layout-gap, 0px) * (12 - 4) / 12));
    }
    &.span-desktop-5 {
      flex: 0 0 calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
      max-width: calc((100% / 12 * 5) - (var(--flex-layout-gap, 0px) * (12 - 5) / 12));
    }
    &.span-desktop-6 {
      flex: 0 0 calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
      max-width: calc((100% / 12 * 6) - (var(--flex-layout-gap, 0px) * (12 - 6) / 12));
    }
    &.span-desktop-7 {
      flex: 0 0 calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
      max-width: calc((100% / 12 * 7) - (var(--flex-layout-gap, 0px) * (12 - 7) / 12));
    }
    &.span-desktop-8 {
      flex: 0 0 calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
      max-width: calc((100% / 12 * 8) - (var(--flex-layout-gap, 0px) * (12 - 8) / 12));
    }
    &.span-desktop-9 {
      flex: 0 0 calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
      max-width: calc((100% / 12 * 9) - (var(--flex-layout-gap, 0px) * (12 - 9) / 12));
    }
    &.span-desktop-10 {
      flex: 0 0 calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
      max-width: calc((100% / 12 * 10) - (var(--flex-layout-gap, 0px) * (12 - 10) / 12));
    }
    &.span-desktop-11 {
      flex: 0 0 calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
      max-width: calc((100% / 12 * 11) - (var(--flex-layout-gap, 0px) * (12 - 11) / 12));
    }
    &.span-desktop-12 {
      flex: 0 0 100%;
      max-width: 100%;
    }
    &.span-desktop-0 {
      display: none;
    }
  }

  &.align-self-start {
    align-self: flex-start;
  }

  &.align-self-center {
    align-self: center;
  }

  &.align-self-end {
    align-self: flex-end;
  }

  &.align-self-stretch {
    align-self: stretch;
  }

  &.align-self-baseline {
    align-self: baseline;
  }

  &.grow-1 {
    flex-grow: 1;
  }

  &.grow-2 {
    flex-grow: 2;
  }

  &.grow-3 {
    flex-grow: 3;
  }

  &.grow-4 {
    flex-grow: 4;
  }

  &.shrink-1 {
    flex-shrink: 1;
  }

  &.shrink-0 {
    flex-shrink: 0;
  }
}
</style>
